import { initializeApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  type Messaging
} from 'firebase/messaging';
import { FirebaseMessagePayload } from '../types/fcm.types';

// Get environment from NODE_ENV
const isProduction = process.env.REACT_APP_FIREBASE_CONFIG === 'prod';

const VAPID_KEY = isProduction
  ? 'BHkuwAI25AsB-ebQhqHfvqySksjHlSjSiOHlOWPyGKxUuJk7KZcPpfWLnAW5j-nMwaADlzx7l3ukY6nWnB4Qs2w'
  : 'BEh1Gx3u6nDVast9LraZimNPQlShDH1V8pKyzeFuHJAynrCpcQ6ARXgi1roi_TCdoh2avwxBM73bIuhB6Bt0ROo';

// Firebase configuration
const devConfig = {
  apiKey: 'AIzaSyDOZrHjbfr0vKdEMGVcJFxDHvFAqUdVEfE',
  authDomain: 'ayeface-development-env.firebaseapp.com',
  projectId: 'ayeface-development-env',
  storageBucket: 'ayeface-development-env.firebasestorage.app',
  messagingSenderId: '676944474722',
  appId: '1:676944474722:web:9e365188854dd521ead73b',
  measurementId: 'G-6Y3G0XZZTT'
};

const prodConfig = {
  apiKey: 'AIzaSyAQfQohtIjReBmA_L21TcJLR4x995FACc4',
  authDomain: 'aye-face-production.firebaseapp.com',
  projectId: 'aye-face-production',
  storageBucket: 'aye-face-production.firebasestorage.app',
  messagingSenderId: '548053797953',
  appId: '1:548053797953:web:bdb85d6543f68ce824cac0',
  measurementId: 'G-YTNS4KSRQL'
};

const firebaseConfig = isProduction ? prodConfig : devConfig;

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);

// Initialize Firebase Cloud Messaging
let messaging: Messaging | null = null;

try {
  // Check if we're in a browser environment and messaging is supported
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    messaging = getMessaging(firebaseApp);
  }
} catch (error) {
  console.warn('Firebase messaging not supported in this environment:', error);
}

/**
 * Request notification permission and get FCM token
 */
export const requestNotificationPermission = async (): Promise<
  string | null
> => {
  if (!messaging) {
    console.warn('Firebase messaging not initialized');
    return null;
  }

  try {
    // Request permission
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY
      });

      if (token) {
        return token;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

/**
 * Listen for foreground messages
 */
export const onForegroundMessage = (
  callback: (payload: FirebaseMessagePayload) => void
) => {
  if (!messaging) {
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    callback(payload as FirebaseMessagePayload);
  });
};

/**
 * Get current FCM token
 */
export const getCurrentToken = async (): Promise<string | null> => {
  if (!messaging) {
    return null;
  }

  try {
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY
    });
    return token || null;
  } catch (error) {
    console.error('Error getting current FCM token:', error);
    return null;
  }
};

/**
 * Check if notifications are supported
 */
export const isNotificationSupported = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    messaging !== null
  );
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = (): NotificationPermission => {
  if (typeof window !== 'undefined' && 'Notification' in window) {
    return Notification.permission;
  }
  return 'default';
};

export { firebaseApp, messaging };
